import React, { JSX, useState } from "react";
import { useDispatch } from "react-redux";
import { Autocomplete, Typography } from "@mui/material";
import { getStockProducts } from "../../../api/stockProducts";
import { ProductInterface } from "../../../interfaces/ProductInterface";
import * as NotificationActions from "../../../actions/notification";
import DebouncedTextField from "../../../components/DebouncedTextField";

interface ProductNewInterface extends ProductInterface {
  inputValue?: string;
}

interface ProductSelectProps {
  onChange: (value: ProductNewInterface) => void;
}

const ProductSelector = ({ onChange }: ProductSelectProps): JSX.Element => {
  const dispatch = useDispatch();

  const [stockProducts, setStockProducts] = React.useState<ProductNewInterface[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ProductNewInterface | null>(null);

  const textChangeHandler = async ({ target }) => {
    try {
      const query = (target.value || "").trim();
      if (query.length > 2) {
        const productList = (await getStockProducts({
          search: query,
          transactionType: 1,
          limit: 20,
          intangible: false,
          excludeStockAndRates: true
        })) as ProductInterface[];
        if (productList?.length) {
          setStockProducts(productList as ProductNewInterface[]);
        } else {
          setStockProducts([]);
        }
      }
    } catch (error) {
      dispatch(
        NotificationActions.notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: "Sorry !, something went wrong while getting products.",
          autoTimeout: true
        })
      );
    }
  };

  return (
    <Autocomplete
      options={stockProducts}
      clearOnBlur
      fullWidth
      freeSolo
      sx={{ width: 500 }}
      value={selectedProduct}
      onChange={(_, val) => {
        if (typeof val === "string") {
          return;
        }
        onChange(val);
        setSelectedProduct(val);
      }}
      renderInput={(params) => (
        <DebouncedTextField
          debounceAt={1000}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...params}
          placeholder="Select Product"
          label="Select additional items"
          variant="outlined"
          margin="dense"
          slotProps={{
            inputLabel: { shrink: true }
          }}
          onFocus={(e) => e.target.select()}
          onChange={textChangeHandler}
        />
      )}
      renderOption={(ownProps, option) => (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <li
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...ownProps}
          key={option.id || "customFilterKey"}
          style={{
            display: "flex",
            justifyContent: "space-between"
          }}
          onClick={(e) => {
            ownProps.onClick(e);
            e.stopPropagation();
          }}
          onKeyDown={null}
        >
          <Typography style={{ width: "80%" }}>{option?.name}</Typography>
        </li>
      )}
      getOptionLabel={(option) => option.name}
    />
  );
};

export default ProductSelector;
